import { useState, useEffect, useCallback } from 'react';
import { listAPIKeys, createAPIKey, revokeAPIKey, APIKey, NewAPIKey } from '../services/dashboardApi';

interface APIKeysProps {
  userId: string;
}

export function APIKeys({ userId }: APIKeysProps) {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<NewAPIKey | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchAPIKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listAPIKeys(userId);
      setApiKeys(data.apiKeys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAPIKeys();
  }, [fetchAPIKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      const key = await createAPIKey(userId, newKeyName.trim());
      setNewKey(key);
      setNewKeyName('');
      fetchAPIKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (keyId: string) => {
    try {
      setDeleting(true);
      await revokeAPIKey(userId, keyId);
      setDeleteConfirm(null);
      fetchAPIKeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for HTTP
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="portal-surface" style={styles.container}>
        <div style={styles.loading}>Loading API keys...</div>
      </div>
    );
  }

  return (
    <div className="portal-surface" style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Access Keys</h1>
          <p style={styles.subtitle}>
            Create and control secure app access to your Media Magic assets.
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create New Key
        </button>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} style={styles.dismissButton}>
            Dismiss
          </button>
        </div>
      )}

      {/* API Keys List */}
      <div style={styles.keysContainer}>
        {apiKeys.length === 0 ? (
          <div style={styles.emptyState}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--line-strong)" strokeWidth="1.5">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
            </svg>
            <h3 style={styles.emptyTitle}>No Access Keys Yet</h3>
            <p style={styles.emptyText}>
              Create an API key to start accessing your images programmatically
            </p>
            <button onClick={() => setShowCreateModal(true)} style={styles.createButton}>
              Create Your First Key
            </button>
          </div>
        ) : (
          <div style={styles.keysList}>
            {apiKeys.map((key) => (
              <div key={key.keyId} style={styles.keyCard}>
                <div style={styles.keyHeader}>
                  <div style={styles.keyInfo}>
                    <h3 style={styles.keyName}>{key.name}</h3>
                    <code style={styles.maskedKey}>{key.maskedKey}</code>
                  </div>
                  <span style={{
                    ...styles.statusBadge,
                    backgroundColor: key.status === 'active' ? 'var(--success-100)' : 'var(--danger-100)',
                    color: key.status === 'active' ? 'var(--success-700)' : 'var(--danger-700)',
                  }}>
                    {key.status}
                  </span>
                </div>
                <div style={styles.keyMeta}>
                  <span style={styles.metaItem}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Created {formatDate(key.createdAt)}
                  </span>
                  <span style={styles.metaItem}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"></path>
                    </svg>
                    {key.rateLimit.toLocaleString()} requests/hour
                  </span>
                </div>
                <div style={styles.keyActions}>
                  <button
                    onClick={() => setDeleteConfirm(key.keyId)}
                    style={styles.revokeButton}
                  >
                    Revoke Key
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => !creating && setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Create New API Key</h2>
            <p style={styles.modalText}>
              Give your API key a name to help you identify it later.
            </p>
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="e.g., Production Server"
              style={styles.input}
              autoFocus
              disabled={creating}
            />
            <div style={styles.modalActions}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={styles.cancelButton}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                style={styles.confirmButton}
                disabled={creating || !newKeyName.trim()}
              >
                {creating ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Key Display Modal */}
      {newKey && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>API Key Created</h2>
            <div style={styles.warningBanner}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span>Copy this key now. It won't be shown again!</span>
            </div>
            <div style={styles.keyDisplay}>
              <code style={styles.fullKey}>{newKey.apiKey}</code>
              <button
                onClick={() => copyToClipboard(newKey.apiKey)}
                style={styles.copyButton}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div style={styles.modalActions}>
              <button
                onClick={() => setNewKey(null)}
                style={styles.confirmButton}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={styles.modalOverlay} onClick={() => !deleting && setDeleteConfirm(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Revoke API Key?</h2>
            <p style={styles.modalText}>
              This action cannot be undone. Any applications using this key will lose access immediately.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={() => setDeleteConfirm(null)}
                style={styles.cancelButton}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                style={styles.deleteButton}
                disabled={deleting}
              >
                {deleting ? 'Revoking...' : 'Revoke Key'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: 'clamp(14px, 3vw, 24px)',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: 'var(--ink-900)',
    margin: 0,
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--ink-600)',
    marginTop: '4px',
  },
  createButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    backgroundColor: 'var(--primary-600)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--ink-600)',
  },
  errorBanner: {
    backgroundColor: 'var(--danger-100)',
    color: 'var(--danger-700)',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--danger-700)',
    cursor: 'pointer',
    fontWeight: '500',
  },
  keysContainer: {
    marginBottom: '32px',
  },
  emptyState: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: 'clamp(22px, 5vw, 48px)',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--ink-900)',
    marginTop: '16px',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: 'var(--ink-600)',
    marginBottom: '24px',
  },
  keysList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  keyCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  keyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '12px',
  },
  keyInfo: {
    flex: 1,
    minWidth: 0,
  },
  keyName: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--ink-900)',
    margin: '0 0 4px 0',
  },
  maskedKey: {
    fontSize: '13px',
    color: 'var(--ink-600)',
    backgroundColor: 'var(--surface-1)',
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    display: 'inline-block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'top',
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
  },
  keyMeta: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    marginBottom: '12px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: 'var(--ink-600)',
  },
  keyActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: '1px solid var(--surface-1)',
    paddingTop: '12px',
    marginTop: '4px',
  },
  revokeButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    color: 'var(--danger-700)',
    border: '1px solid var(--danger-700)',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '480px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: 'var(--ink-900)',
    margin: '0 0 8px 0',
  },
  modalText: {
    fontSize: '14px',
    color: 'var(--ink-600)',
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid var(--line-soft)',
    borderRadius: '6px',
    marginBottom: '16px',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: 'var(--ink-600)',
    border: '1px solid var(--line-soft)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  confirmButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--primary-600)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  deleteButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--danger-700)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  warningBanner: {
    backgroundColor: 'var(--warning-100)',
    color: 'var(--warning-700)',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  keyDisplay: {
    backgroundColor: 'var(--surface-1)',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  fullKey: {
    flex: 1,
    fontSize: '13px',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  copyButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--primary-600)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    whiteSpace: 'nowrap',
  },
};
